import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Formate une date en format français (JJ/MM/AAAA) avec fuseau horaire Europe/Paris
 * @param {Date|string} date - La date à formater
 * @returns {string} - Date formatée en JJ/MM/AAAA
 */
export function formatDateFR(date) {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(dateObj);
}

/**
 * Formate une date en format français long avec fuseau horaire Europe/Paris
 * Exemple: "lundi 14 octobre 2025"
 * @param {Date|string} date - La date à formater
 * @returns {string} - Date formatée en format long
 */
export function formatDateLongFR(date) {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(dateObj);
}

/**
 * Formate une date et heure en format français avec fuseau horaire Europe/Paris
 * Exemple: "14/10/2025 14:30"
 * @param {Date|string} date - La date à formater
 * @returns {string} - Date et heure formatées
 */
export function formatDateTimeFR(date) {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
}

/**
 * Retourne le jour de la semaine en français
 * Exemple: "Lundi"
 * @param {Date|string} date - La date
 * @returns {string} - Jour de la semaine
 */
export function getWeekdayFR(date) {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long'
  }).format(dateObj);
}

/**
 * Obtient la date/heure actuelle en fuseau Paris
 * @returns {Date} - Date actuelle en fuseau Europe/Paris
 */
export function getNowParis() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
}

/**
 * Convertit une date en format ISO (YYYY-MM-DD) en tenant compte du fuseau Paris
 * @param {Date} date - La date à convertir (optionnel, défaut = maintenant)
 * @returns {string} - Date au format YYYY-MM-DD
 */
export function getDateISOParis(date = null) {
  const targetDate = date || getNowParis();
  const parisDate = new Date(targetDate.toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
  
  const year = parisDate.getFullYear();
  const month = String(parisDate.getMonth() + 1).padStart(2, '0');
  const day = String(parisDate.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Vérifie si le stock d'un produit est faible
 * @param {Array} productCatalog - Le catalogue de produits
 * @param {number} threshold - Seuil de stock faible (défaut: 2)
 * @returns {boolean} - true si au moins un produit a un stock faible
 */
export function checkLowStock(productCatalog, threshold = 2) {
  if (!productCatalog || productCatalog.length === 0) return false;
  return productCatalog.some(product => 
    product.quantite_stock !== undefined && 
    product.quantite_stock !== null && 
    product.quantite_stock < threshold
  );
}

/**
 * Empêche le scroll automatique en haut de page lors des soumissions de formulaire
 * Utiliser avec: onSubmit={(e) => { e.preventDefault(); handleSubmitWithScrollPreserve(e, yourFunction); }}
 * @param {Event} event - L'événement de soumission
 * @param {Function} callback - La fonction à appeler après avoir empêché le scroll
 */
export function handleSubmitWithScrollPreserve(event, callback) {
  event.preventDefault();
  const scrollPosition = window.scrollY;
  
  // Exécuter le callback
  const result = callback(event);
  
  // Si c'est une promesse, attendre puis restaurer le scroll
  if (result && typeof result.then === 'function') {
    result.then(() => {
      window.scrollTo(0, scrollPosition);
    });
  } else {
    // Sinon restaurer immédiatement
    setTimeout(() => window.scrollTo(0, scrollPosition), 0);
  }
  
  return result;
}

/**
 * Hook React pour préserver la position du scroll
 * Utilisation: const preserveScroll = usePreserveScroll();
 * Puis: onClick={() => preserveScroll(yourFunction)}
 */
export function preserveScrollPosition(callback) {
  const scrollPosition = window.scrollY;
  
  const result = callback();
  
  if (result && typeof result.then === 'function') {
    result.then(() => {
      requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
    });
  } else {
    requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
  }
  
  return result;
}
